import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Card, Col, Empty, Row, Table, Typography } from 'antd'
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { getRevenueMetrics, MetricsPeriod, MrrSummary } from '../../../Actions/operationsMetricsApi'
import StatCard from './StatCard'
import { formatCurrency, formatNumber, formatRate } from './formatters'

const PIE_COLORS = ['#1677ff', '#52c41a', '#faad14', '#eb2f96', '#722ed1', '#13c2c2', '#fa541c', '#a0d911']

const mrrColumns = [
  {
    title: '통화',
    dataIndex: 'currencyCode',
    key: 'currencyCode',
    render: (currencyCode: string | null) => currencyCode || 'KRW',
  },
  {
    title: '결제방식',
    dataIndex: 'manualPayment',
    key: 'manualPayment',
    render: (manual: boolean) => (manual ? '수동(계약)' : '자동(PG)'),
  },
  {
    title: 'MRR',
    dataIndex: 'mrr',
    key: 'mrr',
    align: 'right' as const,
    render: (value: number, row: MrrSummary) => formatCurrency(row.currencyCode, value),
  },
  {
    title: 'ARR',
    dataIndex: 'arr',
    key: 'arr',
    align: 'right' as const,
    render: (value: number, row: MrrSummary) => formatCurrency(row.currencyCode, value),
  },
]

interface Props {
  period: MetricsPeriod
  prevPeriod: MetricsPeriod
}

/** 💰 매출 — 유료 워크스페이스 KPI(전기간 대비), 플랜 분포 도넛, 통화·결제방식별 MRR/ARR */
export default function RevenueSection({ period, prevPeriod }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['operationsMetrics', 'revenue', period],
    queryFn: () => getRevenueMetrics(period),
  })
  const { data: prev } = useQuery({
    queryKey: ['operationsMetrics', 'revenue', prevPeriod],
    queryFn: () => getRevenueMetrics(prevPeriod),
  })

  // 통화 미저장(null) 행과 'KRW' 행이 따로 그룹핑돼 같은 KRW가 여러 줄로 보이는 걸
  // 정규화 통화+결제방식 기준으로 합산해 한 줄로 합친다 (전부 KRW 전제)
  const mrrRows = useMemo(() => {
    const map = (data?.mrr ?? []).reduce((acc, row) => {
      const currencyCode = row.currencyCode || 'KRW'
      const key = `${currencyCode}:${row.manualPayment}`
      const cur = acc.get(key) ?? { currencyCode, manualPayment: row.manualPayment, mrr: 0, arr: 0 }
      acc.set(key, { ...cur, mrr: cur.mrr + row.mrr, arr: cur.arr + row.arr })
      return acc
    }, new Map<string, MrrSummary>())
    return Array.from(map.values())
  }, [data])

  if (error) return <Alert type="error" message="매출 지표를 불러오지 못했습니다." description={(error as Error).message} />

  const planData = (data?.planDistribution ?? []).filter((p) => p.workspaceCount > 0)

  return (
    <Card title="💰 매출 / 유료 전환" size="small">
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}>
          <StatCard
            title="유료 워크스페이스"
            value={data ? formatNumber(data.activePaidWorkspaces) : '—'}
            rawValue={data?.activePaidWorkspaces}
            previous={prev?.activePaidWorkspaces}
            loading={isLoading}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="신규 유료 전환"
            value={data ? formatNumber(data.newPaidWorkspaces) : '—'}
            rawValue={data?.newPaidWorkspaces}
            previous={prev?.newPaidWorkspaces}
            valueColor="#3f8600"
            loading={isLoading}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="이탈"
            value={data ? formatNumber(data.churnedPaidWorkspaces) : '—'}
            rawValue={data?.churnedPaidWorkspaces}
            previous={prev?.churnedPaidWorkspaces}
            valueColor="#cf1322"
            loading={isLoading}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="유료 이탈률"
            value={data?.churnRate != null ? formatRate(data.churnRate) : '—'}
            suffix={
              data?.churnRate != null ? (
                <span style={{ fontSize: 12, color: '#999' }}>/ base {formatNumber(data.activePaidAtStart)}</span>
              ) : undefined
            }
            valueColor="#cf1322"
            loading={isLoading}
          />
        </Col>
      </Row>
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} md={10}>
          <Typography.Text strong>플랜별 유료 워크스페이스 분포</Typography.Text>
          {planData.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="데이터 없음" />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={planData}
                  dataKey="workspaceCount"
                  nameKey="planName"
                  innerRadius={50}
                  outerRadius={80}
                >
                  {planData.map((entry, index) => (
                    <Cell key={entry.planName} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Legend
                  formatter={(value, entry) =>
                    `${value} (${formatNumber(Number((entry?.payload as { workspaceCount?: number })?.workspaceCount ?? 0))})`
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Col>
        <Col xs={24} md={14}>
          <Typography.Text strong>MRR / ARR (현재 활성 구독 기준 · 통화별 분리)</Typography.Text>
          <Table
            rowKey={(row: MrrSummary) => `${row.currencyCode}:${row.manualPayment}`}
            columns={mrrColumns}
            dataSource={mrrRows}
            size="small"
            pagination={false}
            loading={isLoading}
            style={{ marginTop: 8 }}
          />
        </Col>
      </Row>
    </Card>
  )
}
